import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { noop } from 'lodash';
import classNames from 'classnames';

import { useAppModal } from '@/provider/AppModal/AppModal.context';
import { useKeyboardShortcut } from '@/hook/useKeyboardShortcut';

type ModalViewProps = PropsWithChildren<{
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    closeable?: boolean;
    bgContainerClassName?: string;
    containerClassName?: string;
    contentContainerClassName?: string;
    onClose: () => void;
    onSubmit?: () => void;
    onAnimationStart?: () => void;
}>;

export const ModalView: FC<ModalViewProps> = ({
    children,
    maxWidth = '2xl',
    closeable = true,
    onClose,
    onSubmit,
    onAnimationStart,
    bgContainerClassName,
    containerClassName,
    contentContainerClassName,
}) => {
    const { setDialogElementRef } = useAppModal();

    const containerView = useRef<HTMLDialogElement>(null);

    const close = () => {
        if (closeable) {
            onClose?.();
        }
    };

    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        setDialogElementRef(containerView);
    }, []);

    useKeyboardShortcut({
        shortcuts: {
            ['Escape']: close,
            ['Enter']: onSubmit,
        },
        isActive: mounted,
    });

    const maxWidthClass = {
        sm: 'md:max-w-sm',
        md: 'md:max-w-md',
        lg: 'md:max-w-lg',
        xl: 'md:max-w-xl',
        '2xl': 'md:max-w-2xl',
    }[maxWidth];

    // NOTE: Using standard dialog element instead of div to avoid issues with accessibility & tab indexes
    // See more: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dialog
    // NOTE: The modal used to blink at the end of its fade in/out. framer-motion
    //   runs opacity as a hardware-accelerated (WAAPI) animation and writes the
    //   final value a frame after it ends, so the dialog showed its old opacity
    //   for a frame -- and "transition-all" replayed the fade from there.
    //   "onUpdate" opts out of the accelerated path; keep "transition-*" off
    //   the dialog.
    return (
        <motion.dialog
            ref={containerView}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onUpdate={noop}
            className={classNames(
                'fixed w-full h-full max-w-full max-h-full top-0 left-0 right-0 bottom-0 flex overflow-y-auto p-0 m-0 items-center z-50 transform bg-black/40 backdrop-blur-sm',
                containerClassName,
            )}
            onAnimationStart={onAnimationStart}>
            <div
                onClick={close}
                className={classNames(
                    'absolute w-full h-full',
                    bgContainerClassName,
                )}
            />
            <div
                className={classNames(
                    `mb-6 bg-neutral-light rounded-lg overflow-hidden shadow-extra-large transform transition-all w-11/12 mx-auto`,
                    maxWidthClass,
                    contentContainerClassName,
                )}>
                {children}
            </div>
        </motion.dialog>
    );
};
